import { forwardRef, useMemo } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useDemo } from '@/contexts/DemoContext';
import { DemoConversionCta } from './DemoConversionCta';
import { calculateDaysRemaining } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

interface DemoLeaderboardEntry {
  user_id: string;
  profile_user_id: string | null;
  beer_count: number;
  wine_count: number;
  shot_count: number;
  total: number;
  longest_streak: number;
  rank: number;
}

function DemoLeaderboardRow({ 
  entry, isCurrentUser, t 
}: { 
  entry: DemoLeaderboardEntry; 
  isCurrentUser: boolean;
  t: (key: string) => string;
}) {
  const displayName = entry.profile_user_id || 'Anonymous';
  const isOverLimit = entry.total > 100;
  
  return (
    <div 
      className={`grid grid-cols-[minmax(0,1fr)_40px_36px_28px_24px_24px] gap-1.5 py-2 rounded-md px-1 ${
        isCurrentUser ? 'bg-primary/5' : ''
      } ${isOverLimit ? 'bg-destructive/5' : ''}`}
    >
      <div className="flex items-center gap-1 min-w-0">
        <span className={`text-xs sm:text-sm font-medium w-5 shrink-0 tabular-nums ${isOverLimit ? 'text-destructive' : ''}`}>
          {entry.rank}.
        </span>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className={`text-xs sm:text-sm font-medium truncate ${isOverLimit ? 'text-destructive' : ''}`}>
              {displayName}
            </span>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-[200px]">
            <p className="break-words">{displayName}</p>
          </TooltipContent>
        </Tooltip>
        {isCurrentUser && (
          <span className="text-[10px] text-muted-foreground shrink-0 ml-0.5">{t('leaderboardYou')}</span>
        )}
      </div>
      <span className={`text-center text-xs sm:text-sm font-bold tabular-nums ${isOverLimit ? 'text-destructive' : ''}`}>{entry.total}</span>
      <span className={`text-center text-xs sm:text-sm tabular-nums ${isOverLimit ? 'text-destructive' : ''}`}>{entry.longest_streak}</span>
      <span className={`text-center text-xs sm:text-sm tabular-nums ${isOverLimit ? 'text-destructive' : ''}`}>{entry.beer_count}</span>
      <span className={`text-center text-xs sm:text-sm tabular-nums ${isOverLimit ? 'text-destructive' : ''}`}>{entry.wine_count}</span>
      <span className={`text-center text-xs sm:text-sm tabular-nums ${isOverLimit ? 'text-destructive' : ''}`}>{entry.shot_count}</span>
    </div>
  );
}

export const DemoLeaderboardSection = forwardRef<HTMLElement>(
  function DemoLeaderboardSection(_, ref) {
    const { t } = useLanguage();
    const { mockUsers, mockGroup } = useDemo();

    const daysRemaining = calculateDaysRemaining(mockGroup.challenge_end);

    // Convert mock users to leaderboard entries with global ranking
    const { sectionA, sectionB } = useMemo(() => {
      const entries: DemoLeaderboardEntry[] = mockUsers.map((user, index) => ({
        user_id: user.id,
        profile_user_id: user.user_id,
        beer_count: user.beer,
        wine_count: user.wine,
        shot_count: user.shot,
        total: user.totalDrinks,
        longest_streak: user.longestStreak,
        rank: index + 1,
      }));

      return {
        sectionA: entries.filter(e => e.total <= 100),
        sectionB: entries.filter(e => e.total > 100),
      };
    }, [mockUsers]);

    return (
      <section ref={ref} id="leaderboard" className="py-8 border-t border-border">
        {/* Section Header */}
        <div className="px-6 pb-4 text-center max-w-lg mx-auto">
          <h2 className="text-xl font-serif font-bold mb-2">{t('leaderboardTitle')}</h2>
          <p className="text-sm text-muted-foreground">
            {daysRemaining} {t('leaderboardDaysRemaining')}
          </p>
        </div>

        {/* Leaderboard Table */}
        <div className="px-6 max-w-lg mx-auto">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {t('leaderboardRankings')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-0.5">
                {/* Header row */}
                <div className="grid grid-cols-[minmax(0,1fr)_40px_36px_28px_24px_24px] gap-1.5 text-xs text-muted-foreground font-medium pb-1.5 border-b border-border/60 px-1">
                  <span>{t('leaderboardUser')}</span>
                  <span className="text-center font-bold">{t('leaderboardTotal')}</span>
                  <span className="text-center">🗓️</span>
                  <span className="text-center">🍺</span>
                  <span className="text-center">🍷</span>
                  <span className="text-center">🥃</span>
                </div>

                {/* Section A: Users with ≤100 drinks */}
                {sectionA.map((entry) => (
                  <DemoLeaderboardRow
                    key={entry.user_id}
                    entry={entry}
                    isCurrentUser={entry.user_id === 'demo'}
                    t={t}
                  />
                ))}

                {/* Red divider between sections */}
                <div className="border-b-[3px] border-destructive my-2" />

                {/* Section B: Users with >100 drinks */}
                {sectionB.map((entry) => (
                  <DemoLeaderboardRow
                    key={entry.user_id}
                    entry={entry}
                    isCurrentUser={entry.user_id === 'demo'}
                    t={t}
                  />
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Conversion CTA */}
        <DemoConversionCta variant="inline" className="mt-6" />
      </section>
    );
  }
);
